import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
  deletePost,
  getManagedPosts,
  getSettings,
  updatePost,
} from "../utils/apiClient";
import { normalizeVisibility, VISIBILITY_OPTIONS } from "../post/compositions";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const VISIBILITY_CHIP = {
  private: { label: "Privat", color: "default" },
  users: { label: "Brugere", color: "info" },
  public: { label: "Offentlig", color: "success" },
};

function ManagePostsPanel() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyUuid, setBusyUuid] = useState("");
  const [allowedVisibilities, setAllowedVisibilities] = useState(
    VISIBILITY_OPTIONS.map((o) => o.value)
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSettings();
        if (cancelled) return;
        const allowed = data?.topic_visibility?.home;
        if (Array.isArray(allowed) && allowed.length) {
          setAllowedVisibilities(allowed);
        }
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getManagedPosts();
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const handleVisibilityChange = async (post, visibility) => {
    setBusyUuid(post.post_uuid);
    setError("");
    try {
      await updatePost(post.permalink || post.post_uuid, { visibility });
      await loadPosts();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Update failed");
    } finally {
      setBusyUuid("");
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Slet “${post.title}”?`)) return;
    setBusyUuid(post.post_uuid);
    setError("");
    try {
      await deletePost(post.permalink || post.post_uuid);
      await loadPosts();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Delete failed");
    } finally {
      setBusyUuid("");
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            Administrer indlæg
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ret synlighed, åbn editoren eller slet indlæg.
          </Typography>
        </Box>
        <Button
          variant="contained"
          component={RouterLink}
          to="/admin/posts/new"
          sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          Nyt indlæg
        </Button>
      </Stack>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !posts.length ? (
        <Typography color="text.secondary">Ingen indlæg endnu. Opret dit første.</Typography>
      ) : (
        <Stack spacing={0}>
          {posts.map((post) => {
            const busy = busyUuid === post.post_uuid;
            const visibility = normalizeVisibility(post.visibility);
            const chip = VISIBILITY_CHIP[visibility] || VISIBILITY_CHIP.private;
            return (
              <Box
                key={post.post_uuid}
                sx={{
                  py: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={chip.label}
                      color={chip.color}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {post.published_at
                        ? `Shared ${formatDate(post.published_at)}`
                        : `Created ${formatDate(post.created_at)}`}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 650,
                      cursor: "pointer",
                      "&:hover": { color: "primary.main" },
                    }}
                    onClick={() => navigate(`/posts/${post.permalink || post.post_uuid}`)}
                  >
                    {post.title}
                  </Typography>
                  {post.excerpt ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {post.excerpt}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Select
                    size="small"
                    value={visibility}
                    disabled={busy}
                    onChange={(e) => handleVisibilityChange(post, e.target.value)}
                    sx={{ minWidth: 110 }}
                  >
                    {VISIBILITY_OPTIONS.filter(
                      (opt) =>
                        allowedVisibilities.includes(opt.value) || opt.value === visibility
                    ).map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <IconButton
                    aria-label="Rediger indlæg"
                    disabled={busy}
                    onClick={() => navigate(`/admin/posts/${post.post_uuid}/edit`)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Slet indlæg"
                    disabled={busy}
                    onClick={() => handleDelete(post)}
                    color="error"
                  >
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export default ManagePostsPanel;
